import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { useSelector } from "react-redux";
import { persistor, RootState, store } from "./redux/store";
import { SnackbarProvider } from "notistack";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import darkTheme from "./themes/dark-theme";
import lightTheme from "./themes/light-theme";
import App from "./app";
import {
  PublicClientApplication,
  EventMessage,
  EventType,
  AuthenticationResult,
  InteractionRequiredAuthError,
} from "@azure/msal-browser";
import { loginRequest, msalConfig } from "./shared/app-configs/authconfig";
import { BrowserRouter, useNavigate } from "react-router-dom";
import { MsalProvider, useMsal } from "@azure/msal-react";
import { AppConfig } from "./shared/app-configs/app-config";
import { useApi } from "./shared/hooks/useApi";
import { TCAppConfig } from "./shared/app-configs/tc-app-configs";
import { useTransactionCertificate } from "./shared/hooks/transaction-certificate.hooks";
import { useTransactionCertificateStore } from "./store/transaction-certificates.store";

const msalInstance: any = new PublicClientApplication(msalConfig);

const initializeMsal = async () => {
  // msalInstance["browserStorage"].clear();
  await msalInstance.initialize();
  const activeAccount: any = msalInstance.getActiveAccount();

  if (!activeAccount) {
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      msalInstance.setActiveAccount(accounts[0]);
    }
  }

  msalInstance.addEventCallback((event: EventMessage) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
      const authenticationResult = event.payload as AuthenticationResult;
      msalInstance.setActiveAccount(authenticationResult.account);
    }
  });

  msalInstance.enableAccountStorageEvents();
};

export const checkTokenExpiry = async () => {
  // Get the token expiry from your store
  console.log("checkTokenExpiry start");

  try {
    let tokenExpiry: any =
      useTransactionCertificateStore.getState().tokenExpiry;
    const now = new Date().getTime();
    const REFRESH_THRESHOLD = 5 * 60 * 1000; //5 Mins

    // Changed condition: if time until expiry is LESS than threshold, get new token
    if (tokenExpiry - now < REFRESH_THRESHOLD) {
      // Attempt to silently acquire a new token
      const tokenRequest = {
        ...loginRequest,
        account: msalInstance.getAllAccounts()[0], // Use active account if available
      };
      const response: any = await msalInstance
        .acquireTokenSilent(tokenRequest)
        .catch((error: any) => {
          if (error.name === "InteractionRequiredAuthError") {
              msalInstance.loginRedirect(tokenRequest);
          }
        });

      if (response?.idToken) {
        const expiresOn: any = new Date(response.expiresOn).getTime();
        return {
          token: response.idToken,
          expiry: expiresOn,
          isTokenGenerated: true,
        };
      } else {
        localStorage.clear();
        sessionStorage.clear();
        msalInstance.logoutRedirect();
      }
    }

    return { isTokenGenerated: false };
  } catch (error) {
    console.error("Error in checkTokenExpiry:", error);
    if (error instanceof InteractionRequiredAuthError) {
      localStorage.clear();
      sessionStorage.clear();
      msalInstance.logoutRedirect();
    }
    return { isTokenGenerated: false };
  }
};

const MsalHandler: React.FC = () => {
  const { instance, accounts } = useMsal();
  const navigate = useNavigate();
  const { executeGet } = useApi();
  const {
    getUserInfo,
    setUserInfo,
    setAuthToken,
    authToken,
    setTokenExpiry,
    setCbLicensingCode,
  } = useTransactionCertificateStore();
  const getUserDetails = async (userID: string) => {
    const response: any = await executeGet(TCAppConfig.GET_USER_DETAILS, {
      user_id: userID,
    });
    if (response?.status == 200) {
      console.log(`getUserDetails ${JSON.stringify(response)}`);
      const userInfo = {
        userEmail: response?.data?.mail
          ? response?.data?.mail
          : response?.data?.userPrincipalName,
        displayName: response?.data?.displayName,
      };
     
      setUserInfo(userInfo);
      // getCBLicensingCode(userInfo)
    }
  };
  // const getCBLicensingCode = async (userInfo: any) => {
  //   const response: any = await executeGet(TCAppConfig.GET_CB_CO_CODE, { email: userInfo.userEmail });

  //   if (response?.status == 200 && response.data?.length > 0) {
  //     // response.data?.reduce((r:any)=>r).ID
  //     const updatedUserInfo = {
  //       ...userInfo,
  //     }
  //     setUserInfo(updatedUserInfo)
  //     setCbLicensingCode(response.data?.reduce((r: any) => r).ID)
  //   }
  // }

  useEffect(() => {
    if (authToken == "" || authToken == undefined || authToken == null) {
      const accounts = instance.getAllAccounts();
      if (accounts.length > 0 || instance.getActiveAccount()) {
        console.log("User already logged in:", accounts[0]);
      } else {
        instance.loginRedirect();
      }
    }
  },[]);

  // Auto-refresh token every 1 minute to prevent expiry
  useEffect(() => {
    const tokenRefreshInterval = setInterval(async () => {
      const result = await checkTokenExpiry();
      if (result.isTokenGenerated) {
        console.log("Token auto-refreshed successfully");
        setAuthToken(result.token);
        setTokenExpiry(result.expiry);
      }
    }, 60000); // Check every 1 minute

    return () => clearInterval(tokenRefreshInterval);
  }, []);
  useEffect(() => {
    initializeMsal();
    msalInstance.handleRedirectPromise().then((response: any) => {
      if (response && response.account) {
        if (response) {
          const expiresOn: any = new Date(response.expiresOn).getTime();
          setTokenExpiry(expiresOn);
          setAuthToken(response.idToken);
          if(!getUserInfo?.userEmail){
            const account: any = msalInstance.getActiveAccount();
            if (account) {
              // //alert(`inside account ${JSON.stringify(account[0])}`)
              getUserDetails(account.localAccountId);
              // navigate("/cbdashboard", { replace: true });
            }
          }
        }
        // }
      }
    }).catch((error:any) => {
      console.error("Error handling redirect:", error);
    });;

    const account: any = msalInstance.getActiveAccount();
    if (account) {
      // //alert(`inside account ${JSON.stringify(account[0])}`)
      getUserDetails(account.localAccountId);
      // navigate("/cbdashboard", { replace: true });
    } else {
      msalInstance.initialize();
    }
  }, [navigate]);

  return null;
};

// Theme Wrapper Component
const ThemeWrapper: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const themeMode = useSelector((state: RootState) => state.theme.mode);
  return (
    <ThemeProvider theme={themeMode === "light" ? lightTheme : darkTheme}>
      {children}
    </ThemeProvider>
  );
};

initializeMsal();

// Render the app
{
  ReactDOM.render(
    <BrowserRouter>
      <MsalProvider instance={msalInstance}>
        <Provider store={store}>
          <PersistGate loading={null} persistor={persistor}>
            <ThemeWrapper>
              <SnackbarProvider
                preventDuplicate={true}
                maxSnack={3}
                autoHideDuration={3000}
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
              >
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <MsalHandler />
                  <App />
                </LocalizationProvider>
              </SnackbarProvider>
            </ThemeWrapper>
          </PersistGate>
        </Provider>
      </MsalProvider>
    </BrowserRouter>,
    document.getElementById("root")
  );
}

export { msalInstance };